import { SkeletonMainLayout } from 'src/modules/common/skeletons/SkeletonMainLayout/SkeletonMainLayout.tsx';
import { PropsWithChildren, Suspense, useContext } from 'react';
import { ConfigContextProps, ConfigContext } from 'src/configContext.ts';
import { RuntimeContext } from 'src/runtimeContext.ts';
import { Filter, FilterConfig, MandatoryFilterConfig } from 'src/utils/composeFilter';
import { createGaPush } from 'src/utils/createGaPush';
import { createGetComparator } from 'src/createGetComparator';
import { UserSettings } from 'src/types/UserSettings';
import { RegionsEnum } from 'src/generated/operations';
import { getNumberFormat } from 'src/utils/getNumberFormat';
import { getPriceAttributeName, getPriceAttributeId } from 'src/common/getPriceAttributeName.ts';

type Props = {
    userSettings: UserSettings;
    currency: string | null;
    shadowRoot: HTMLElement;
    imageProxyUri: string;
    uri: string;
} & PropsWithChildren;

export const ConfigContextProvider = ({
    userSettings,
    currency,
    shadowRoot,
    imageProxyUri,
    uri,
    children,
}: Props) => {
    const { isRevitPlugin, isRevit6WayFiltered } = useContext(RuntimeContext);

    const revitPipeConnectorsAttribute = isRevit6WayFiltered
        ? [['pipeConnectors', '6560'] as [string, string]]
        : [];

    const revitPipeConnectorsValue: Filter = isRevit6WayFiltered
        ? { pipeConnectors: ['2-way', '3-way'] }
        : {};

    const filterConfig: FilterConfig = isRevitPlugin
        ? [
            ['magiCADStatus', '3482'],
            ...revitPipeConnectorsAttribute,
        ]
        : [['shoppingCenter', '2778']];

    const shoppingCenter: Filter = (userSettings.shoppingCenter ? {
        shoppingCenter: [
            userSettings.shoppingCenter,
            ...(userSettings.currentRegion === RegionsEnum.Am ? ['SaS_APP'] : []),
        ],
    } : {});

    const mandatoryFilterConfig: MandatoryFilterConfig = {
        config: [
            ['company', '7687'],
            ...filterConfig,
        ],
        values: {
            company: ['Belimo'],
            ...(isRevitPlugin ? {
                magiCADStatus: ['active'],
                ...revitPipeConnectorsValue,
            } : shoppingCenter),
        },
    };

    const accessoryPageMandatoryFilterConfig: MandatoryFilterConfig = isRevitPlugin ? {
        config: [
            ['company', '7687'],
            ['magiCADStatus', '3482'],
        ],
        values: {
            company: ['Belimo'],
            magiCADStatus: ['active'],
        },
    } : mandatoryFilterConfig;

    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const value: ConfigContextProps = {
        gaPush: createGaPush(),
        getComparator: createGetComparator(userSettings.currentLocale),
        currency,
        rootElement: shadowRoot,
        userSettings,
        imageProxyUri,
        uri,
        mandatoryFilterConfig,
        accessoryPageMandatoryFilterConfig,
        numberFormat: getNumberFormat(userSettings.currentLocale),
        priceAttributeName: getPriceAttributeName(userSettings.shoppingCenter, currency),
        priceAttributeId: getPriceAttributeId(userSettings.shoppingCenter, currency),
        selectionsDefaults: {
            ...window.VssSelectionsDefaults === undefined ? {
                addToCartButtonEnabled: true,
                closeButtonType: 'ghost-secondary',
                closeButtonSize: 'sm',
                actionButtonType: 'ghost-secondary',
                actionButtonSize: 'sm',
            } : window.VssSelectionsDefaults as ConfigContextProps['selectionsDefaults'],
        },
    };

    return (
        <ConfigContext.Provider value={value}>
            <Suspense fallback={<SkeletonMainLayout/>}>{children}</Suspense>
        </ConfigContext.Provider>
    );
};
